/**
 * Path-based explorer URLs for the Documents library (formerly Docs Hub).
 *
 *   /documents
 *   /documents/<docPath>
 *   /documents/f/<folderPath>
 *   /documents/templates
 *   /documents/templates/<templatePath>
 *   /documents/templates/f/<folderPath>
 *
 * Paths are UNIQUE per organization in Postgres (`docs_org_path_key` /
 * `doc_folders_org_path_key`), so the path alone identifies a row.
 * Reserved first segments under `/documents`: `templates`, `f`.
 * Legacy `/docs` URLs redirect here.
 */

import {
  DOCUMENTS_ROOT,
  docsHubDocumentHref,
  encodeDocumentPath
} from '@atbash-labs/plantview-links'

export const DOCS_HOME = DOCUMENTS_ROOT
export const DOC_TEMPLATES_HOME = '/documents/templates'

/**
 * Shared by the Documents and Templates explorer rails so collapsing one
 * tab keeps the other collapsed after a route swap.
 */
export const DOCS_EXPLORER_OPEN_KEY = 'ontology.documents.explorerSidebar.open'

/** Encode a slash-separated resource path for use in a URL pathname. */
export function encodeResourcePath(path: string): string {
  return encodeDocumentPath(path)
}

/** Decode a splat captured from `/documents/…` or `/documents/f/…`. */
export function decodeResourcePath(splat: string | undefined): string {
  if (splat === undefined || splat === '') return ''
  return splat
    .split('/')
    .filter((segment) => segment.length > 0)
    .map((segment) => {
      try {
        return decodeURIComponent(segment)
      } catch {
        return segment
      }
    })
    .join('/')
}

export function docHref(path: string): string {
  return docsHubDocumentHref(path)
}

export function docFolderHref(path: string): string {
  if (path === '') return DOCS_HOME
  return `${DOCS_HOME}/f/${encodeResourcePath(path)}`
}

export function docTemplateHref(path: string): string {
  const encoded = encodeResourcePath(path)
  if (encoded === '') return DOC_TEMPLATES_HOME
  return `${DOC_TEMPLATES_HOME}/${encoded}`
}

export function docTemplateFolderHref(path: string): string {
  if (path === '') return DOC_TEMPLATES_HOME
  return `${DOC_TEMPLATES_HOME}/f/${encodeResourcePath(path)}`
}
